// kan-111 References tab redesign — shared building blocks for the two
// reference tables (AI Reference Taker + Reference Forms). Styles live in
// ReferenceTables.scss under `.refTabs`; see
// `_design/design_handoff_references_tables` (option 1a).
import { ReactNode } from "react";
import { formatUSPhone } from "../../../utils/commonFn";
import "./ReferenceTables.scss";

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const toDate = (value?: string | Date | null): Date | null => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** `Oct 12, 2026` or null. */
export const formatRefDate = (value?: string | Date | null): string | null => {
  const d = toDate(value);
  return d
    ? d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    : null;
};

/** `2:23 PM` (no seconds) or null. */
export const formatRefTime = (value?: string | Date | null): string | null => {
  const d = toDate(value);
  return d
    ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    : null;
};

/** Always `(XXX) XXX-XXXX` when the number is a 10/11-digit US number. */
export const formatRefPhone = (value?: string | null): string =>
  value ? formatUSPhone(value) || value : "";

/** `{Job Title} · {Relationship}`, skipping blanks. */
export const joinRefMeta = (...parts: Array<string | null | undefined>) =>
  parts
    .map((p) => (p ?? "").trim())
    .filter(Boolean)
    .join(" · ");

// ---------------------------------------------------------------------------
// Layout pieces
// ---------------------------------------------------------------------------

/** Outer white card holding both table sections. */
export const ReferenceTabsCard = ({ children }: { children: ReactNode }) => (
  <div className="refTabs">{children}</div>
);

export const RefSectionHeader = ({
  title,
  count,
  description,
  action,
}: {
  title: string;
  count?: number | null;
  description: string;
  action?: ReactNode;
}) => (
  <div className="refSectionHeader">
    <div className="refSectionTitleBlock">
      <div className="refSectionTitleRow">
        <h5 className="refSectionTitle">{title}</h5>
        {typeof count === "number" && (
          <span className="refCountChip">{count}</span>
        )}
      </div>
      <span className="refSectionDesc">{description}</span>
    </div>
    {action}
  </div>
);

export const RefTableHead = ({ columns }: { columns: string[] }) => (
  <div className="refGridRow refHeadRow" role="row">
    {columns.map((c) => (
      <span key={c} role="columnheader">
        {c}
      </span>
    ))}
  </div>
);

export const RefEmptyRow = ({ text }: { text: string }) => (
  <div className="refEmptyRow">{text}</div>
);

/** `{Title} – {Company}`, either part alone when the other is blank. */
export const joinTitleCompany = (
  title?: string | null,
  company?: string | null
) =>
  [title, company]
    .map((p) => (p ?? "").trim())
    .filter(Boolean)
    .join(" – ");

/** Column 1: name + `{title} – {company} · {relationship}`. */
export const RefNameCell = ({
  name,
  title,
  company,
  relationship,
}: {
  name?: string | null;
  title?: string | null;
  company?: string | null;
  relationship?: string | null;
}) => {
  const meta = joinRefMeta(joinTitleCompany(title, company), relationship);
  return (
    <div className="refCellStack">
      <span className="refName" title={name ?? undefined}>
        {name || "--"}
      </span>
      {meta && (
        <span className="refMeta" title={meta}>
          {meta}
        </span>
      )}
    </div>
  );
};

/** Column 2: email (link blue, ellipsis) + formatted phone. */
export const RefContactCell = ({
  email,
  phone,
}: {
  email?: string | null;
  phone?: string | null;
}) => (
  <div className="refCellStack">
    {email ? (
      <span className="refEmail" title={email}>
        {email}
      </span>
    ) : (
      <span className="refMissing">No email</span>
    )}
    {phone && <span className="refMeta">{formatRefPhone(phone)}</span>}
  </div>
);

/** Two-line date cell. `line2` defaults to the time. `--` when empty. */
export const RefDateCell = ({
  value,
  line1,
  line2,
}: {
  value?: string | Date | null;
  line1?: string | null;
  line2?: string | null;
}) => {
  const first = line1 ?? formatRefDate(value);
  if (!first) return <span className="refDash">--</span>;
  const second = line2 === undefined ? formatRefTime(value) : line2;
  return (
    <div className="refCellStack">
      <span>{first}</span>
      {second && <span className="refMeta">{second}</span>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

export type RefBadgeTone = "neutral" | "info" | "warn" | "active" | "danger";

const TONE_CLASS: Record<RefBadgeTone, string> = {
  neutral: "refBadgeNeutral",
  info: "refBadgeInfo",
  warn: "refBadgeWarn",
  active: "refBadgeActive",
  danger: "refBadgeDanger",
};

export const RefBadge = ({
  tone,
  children,
  detail,
}: {
  tone: RefBadgeTone;
  children: ReactNode;
  detail?: ReactNode;
}) => (
  <div style={{ minWidth: 0 }}>
    <span
      className={`refBadge ${TONE_CLASS[tone]}`}
      title={typeof children === "string" ? children : undefined}
    >
      {children}
    </span>
    {detail && <div className="refBadgeDetail">{detail}</div>}
  </div>
);

// ---------------------------------------------------------------------------
// Icons (Feather-style line icons, 24 viewBox, stroke 2, round caps/joins)
// ---------------------------------------------------------------------------

const svgProps = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

export const EyeIcon = () => (
  <svg {...svgProps}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const PenIcon = () => (
  <svg {...svgProps}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

export const DownloadIcon = () => (
  <svg {...svgProps}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

export const TrashIcon = () => (
  <svg {...svgProps}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

export const HistoryIcon = () => (
  <svg {...svgProps}>
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <polyline points="3 3 3 9 9 9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

export const RefIconButton = ({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: ReactNode;
}) => (
  <button
    type="button"
    className={`refIconBtn${danger ? " refIconBtnDanger" : ""}`}
    title={label}
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

// ---------------------------------------------------------------------------
// Footer (pagination)
// ---------------------------------------------------------------------------

export const REF_PAGE_SIZES = [10, 20, 50];

/**
 * `1–3 of 3` on the left; rows-per-page + Prev/Next on the right. The
 * controls are hidden (count kept) when everything fits on one page at the
 * smallest page size.
 */
export const RefFooter = ({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}) => {
  if (!total) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const showControls = total > REF_PAGE_SIZES[0];
  return (
    <div className="refFooter">
      <span>
        {from}–{to} of {total}
      </span>
      {showControls && (
        <div className="refFooterControls">
          <span>Rows per page</span>
          <select
            className="refPageSize"
            aria-label="Rows per page"
            value={pageSize}
            onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
          >
            {REF_PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="refPageLink"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
          >
            ‹ Prev
          </button>
          <button
            type="button"
            className="refPageLink"
            disabled={page >= lastPage}
            onClick={() => onPageChange(page + 1)}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
};
